import { supabase } from '../lib/supabase';

const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function getToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Session expired. Please re-login.');
  return session.access_token;
}

export async function loadPinterestAccounts(profileId = null) {
  if (!functionsUrl) throw new Error('Pinterest services not configured.');
  const token = await getToken();
  const url = profileId
    ? `${functionsUrl}/pinterest-accounts?profileId=${encodeURIComponent(profileId)}`
    : `${functionsUrl}/pinterest-accounts`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, apikey: anonKey },
  });
  if (!res.ok) throw new Error('Failed to load Pinterest accounts.');
  const json = await res.json();
  return json.accounts || [];
}

export async function loadPinterestBoards(accountId) {
  if (!functionsUrl) throw new Error('Pinterest services not configured.');
  const token = await getToken();
  const res = await fetch(
    `${functionsUrl}/pinterest-boards?accountId=${encodeURIComponent(accountId)}`,
    { headers: { Authorization: `Bearer ${token}`, apikey: anonKey } }
  );
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    if (json?.needsReconnect) {
      const err = new Error(json.error || 'Reconnect needed');
      err.needsReconnect = true;
      throw err;
    }
    throw new Error(json?.error || `Failed to load boards (${res.status})`);
  }
  const json = await res.json();
  return json.boards || [];
}

export async function callPublishPin({ pin, accountId, boardId, scheduledAt }) {
  if (!functionsUrl) throw new Error('Pinterest services not configured.');
  const token = await getToken();

  const body = {
    pinterestAccountId: accountId,
    boardId: boardId || pin?.board_id,
    imageUrl: pin?.image_url,
    title: pin?.pin_title || 'Pinterest Pin',
    description: pin?.pin_description || '',
    link: pin?.article_url || '',
    articleUrl: pin?.article_url || '',
    tags: [],
  };
  if (scheduledAt) body.scheduledAt = scheduledAt;

  const res = await fetch(`${functionsUrl}/pinterest-create-pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = payload?.error?.message || payload?.error || `Pinterest API error (${res.status})`;
    throw new Error(msg);
  }
  return payload;
}

export function extractPinterestUrl(apiResponse) {
  if (!apiResponse) return null;
  const id = apiResponse?.id || apiResponse?.pin?.id || apiResponse?.data?.id;
  const url = apiResponse?.url || apiResponse?.pin?.url || apiResponse?.data?.url;
  if (url) return url;
  if (id) return `https://www.pinterest.com/pin/${id}`;
  return null;
}

export async function updatePinRecord(pinId, updates) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('pinterest_pins')
    .update(updates)
    .eq('id', pinId)
    .eq('user_id', user.id);
  if (error) throw error;
}
